// src/components/sections/DogsSection.jsx

import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import DogProfileCard from "../ui/DogProfileCard";
import { supabase } from "../../lib/supabaseClient";

function normalizeImageUrl(value) {
    if (!value) return "";

    const url = String(value).trim();

    if (
        url.startsWith("http://") ||
        url.startsWith("https://") ||
        url.startsWith("blob:") ||
        url.startsWith("data:")
    ) {
        return url;
    }

    return url.startsWith("/") ? url : `/${url}`;
}

function normalizeSex(value) {
    const sex = String(value ?? "")
        .trim()
        .toLowerCase();

    if (
        sex === "hündin" ||
        sex === "huendin" ||
        sex === "weiblich" ||
        sex === "female"
    ) {
        return "Hündin";
    }

    if (
        sex === "rüde" ||
        sex === "ruede" ||
        sex === "männlich" ||
        sex === "maennlich" ||
        sex === "male"
    ) {
        return "Rüde";
    }

    return value ?? "";
}

function normalizeDog(dog) {
    const name =
        dog.callName ??
        dog.call_name ??
        dog.name ??
        "Unbekannter Hund";

    const rawImage =
        dog.imageSrc ??
        dog.image_src ??
        dog.image ??
        dog.image_url ??
        dog.photo_url ??
        "";

    const imageSrc = normalizeImageUrl(rawImage);

    const height =
        dog.heightCm ??
        dog.height_cm ??
        null;

    const weight =
        dog.weightKg ??
        dog.weight_kg ??
        null;

    const subtitle =
        dog.subtitle ||
        [
            dog.breed,
            dog.color,
            dog.coat,
            height ? `${height} cm` : null,
            weight ? `${weight} kg` : null,
        ]
            .filter(Boolean)
            .join(" · ");

    const badges = Array.isArray(dog.badges)
        ? dog.badges
        : [
            dog.status,
            dog.origin,
            dog.registry,
        ].filter(Boolean);

    const details = Array.isArray(dog.details)
        ? dog.details
        : [
            dog.dob || dog.date_of_birth
                ? {
                    label: "Geboren",
                    value: dog.dob ?? dog.date_of_birth,
                }
                : null,

            dog.health?.patella || dog.patella
                ? {
                    label: "Patella",
                    value: dog.health?.patella ?? dog.patella,
                }
                : null,

            dog.health?.eyes || dog.eyes
                ? {
                    label: "Augen",
                    value: dog.health?.eyes ?? dog.eyes,
                }
                : null,

            dog.father
                ? {
                    label: "Vater",
                    value: dog.father,
                }
                : null,

            dog.mother
                ? {
                    label: "Mutter",
                    value: dog.mother,
                }
                : null,

            dog.breeder
                ? {
                    label: "Züchter",
                    value: dog.breeder,
                }
                : null,
        ].filter(Boolean);

    return {
        ...dog,
        name,
        sex: normalizeSex(dog.sex),
        imageSrc,
        image: imageSrc,
        subtitle,
        badges,
        details,
    };
}

function sortDogs(dogs) {
    return [...dogs].sort((firstDog, secondDog) => {
        const firstOrder =
            firstDog.sort_order === null ||
            firstDog.sort_order === undefined
                ? Number.MAX_SAFE_INTEGER
                : Number(firstDog.sort_order);

        const secondOrder =
            secondDog.sort_order === null ||
            secondDog.sort_order === undefined
                ? Number.MAX_SAFE_INTEGER
                : Number(secondDog.sort_order);

        if (firstOrder !== secondOrder) {
            return firstOrder - secondOrder;
        }

        return String(firstDog.name ?? "").localeCompare(
            String(secondDog.name ?? ""),
            "de",
            {
                sensitivity: "base",
            }
        );
    });
}

export default function DogsSection({
                                        primaryColor,
                                        secondaryColor,
                                        styles,
                                        onDogClick,
                                        sectionId = "huendinnen-rueden",
                                    }) {
    const [dogs, setDogs] = useState([]);
    const [filter, setFilter] = useState("ALLE");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let componentIsMounted = true;

        async function loadDogs() {
            setLoading(true);
            setError("");

            const { data, error: supabaseError } = await supabase
                .from("dogs")
                .select("*")
                .eq("show_in_dogs_section", true)
                .order("sort_order", {
                    ascending: true,
                    nullsFirst: false,
                })
                .order("name", {
                    ascending: true,
                });

            if (!componentIsMounted) return;

            if (supabaseError) {
                console.error(
                    "Fehler beim Laden der Hunde:",
                    supabaseError
                );

                setDogs([]);
                setError(
                    "Die Hunde konnten momentan nicht geladen werden."
                );
            } else {
                const normalizedDogs = (data ?? []).map(normalizeDog);
                setDogs(sortDogs(normalizedDogs));
            }

            setLoading(false);
        }

        loadDogs();

        return () => {
            componentIsMounted = false;
        };
    }, []);

    const filteredDogs = useMemo(() => {
        if (filter === "HUENDINNEN") {
            return dogs.filter(
                (dog) => dog.sex === "Hündin"
            );
        }

        if (filter === "RUEDEN") {
            return dogs.filter(
                (dog) => dog.sex === "Rüde"
            );
        }

        return dogs;
    }, [dogs, filter]);

    const filterButton = (key, label) => {
        const active = filter === key;

        return (
            <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                aria-pressed={active}
                style={{
                    ...styles.dogsFilterButton,
                    borderColor: active
                        ? primaryColor
                        : "#E9E2DC",
                    backgroundColor: active
                        ? primaryColor
                        : "#fff",
                    color: active ? "#fff" : "#5D4037",
                    cursor: "pointer",
                }}
            >
                {label}
            </button>
        );
    };

    return (
        <section
            id={sectionId}
            style={{
                ...styles.section,
                backgroundColor: "#fff",
            }}
        >
            <div
                style={{
                    maxWidth: 1200,
                    margin: "0 auto",
                    padding: "0 clamp(1rem, 4vw, 18px)",
                }}
            >
                <motion.h2
                    style={{
                        ...styles.sectionTitle,
                        color: primaryColor,
                        marginBottom: 10,
                        fontSize: "clamp(2rem, 6vw, 3rem)",
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    Meine Hündinnen &amp; Rüden
                </motion.h2>

                <div
                    style={{
                        height: 2,
                        width: 80,
                        background: primaryColor,
                        borderRadius: 999,
                        margin: "0 auto 18px",
                    }}
                />

                <motion.p
                    style={{
                        ...styles.dogsIntro,
                        maxWidth: 920,
                        margin: "0 auto",
                        lineHeight: 1.75,
                        opacity: 0.92,
                        fontSize: "clamp(1rem, 2.5vw, 1.1rem)",
                    }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    Hier stellen wir unsere Hunde vor – liebevolle
                    Familienmitglieder, sorgfältig ausgewählt und mit
                    Fokus auf Gesundheit, Wesen und typvollem
                    Havaneser-Ausdruck.
                </motion.p>

                <div
                    style={{
                        ...styles.dogsFilterRow,
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: "0.75rem",
                    }}
                >
                    {filterButton("ALLE", "Alle")}
                    {filterButton("HUENDINNEN", "Hündinnen")}
                    {filterButton("RUEDEN", "Rüden")}
                </div>

                {loading && (
                    <div
                        style={{
                            ...styles.dogsEmptyState,
                            textAlign: "center",
                            padding: "2rem",
                        }}
                    >
                        Hunde werden geladen …
                    </div>
                )}

                {!loading && error && (
                    <div
                        style={{
                            ...styles.dogsEmptyState,
                            textAlign: "center",
                            padding: "2rem",
                            color: "#8B2E2E",
                        }}
                    >
                        <strong>{error}</strong>

                        <div
                            style={{
                                marginTop: 6,
                                opacity: 0.9,
                            }}
                        >
                            Bitte versuche es später erneut.
                        </div>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        <div
                            style={{
                                ...styles.dogsGrid,
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                                gap: "1.25rem",
                                alignItems: "stretch",
                            }}
                        >
                            {filteredDogs.map((dog, index) => (
                                <DogProfileCard
                                    key={
                                        dog.id ??
                                        `${dog.name}-${index}`
                                    }
                                    dog={dog}
                                    index={index}
                                    primaryColor={primaryColor}
                                    secondaryColor={secondaryColor}
                                    styles={styles}
                                    onClick={onDogClick}
                                />
                            ))}
                        </div>

                        {filteredDogs.length === 0 && (
                            <div style={styles.dogsEmptyState}>
                                <strong>
                                    Noch keine Einträge.
                                </strong>

                                <div
                                    style={{
                                        marginTop: 6,
                                        opacity: 0.9,
                                    }}
                                >
                                    Aktuell sind keine Hunde für
                                    diese Übersicht freigegeben.
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}